import styled from '@emotion/styled/macro';
import { BaseButton, Variables } from '../../../base';
import { CdCard, CdPanel, CdSectionCard } from './timeline-detail-card.styles';

export const ElPanel = CdPanel;
export const ElCard = CdCard;
export const ElSectionCard = CdSectionCard;

export const ElHeadlineCard = styled(ElCard)<{ $success?: boolean }>`
    padding: 18px 22px;
    border-color: ${({ $success }) =>
        $success ? Variables.ThemeLight.SuccessMild2 : Variables.ThemeLight.Border2};
    background: ${({ $success }) =>
        $success
            ? `color-mix(in srgb, ${Variables.ThemeLight.SuccessMild1} 55%, ${Variables.ThemeLight.Background3})`
            : Variables.ThemeLight.Background3};
`;

export const ElHeadlineRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
`;

export const ElIconWrap = styled.div<{ $success?: boolean }>`
    width: 42px;
    height: 42px;
    border-radius: 999px;
    background: ${({ $success }) =>
        $success ? Variables.ThemeLight.SuccessMild1 : Variables.ThemeLight.AttentionMild1};
    border: 1px solid
        ${({ $success }) =>
            $success ? Variables.ThemeLight.SuccessMild2 : Variables.ThemeLight.AttentionMild2};
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
`;

export const ElHeadlineMain = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
    flex: 1;
`;

export const ElHeadlineTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

export const ElHeadlineTitle = styled.span<{ $success?: boolean }>`
    font-family: ${Variables.Fonts.calibre};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.01em;
    color: ${({ $success }) =>
        $success ? Variables.ThemeLight.SuccessConstant6 : Variables.ThemeLight.TextStrong};
`;

export const ElTitleDot = styled.span`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${Variables.ThemeLight.TextXMild};
`;

export const ElInsuranceType = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 15px;
    font-weight: 500;
    line-height: 1.1;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const ElHeadlineMeta = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
    text-wrap: pretty;
`;

export const ElPayerMuted = styled.span`
    color: ${Variables.ThemeLight.TextMild};
`;

export const ElChipRow = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 2px;
`;

export const ElChip = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    background: ${Variables.ThemeLight.NeutralMild1};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 999px;
    padding: 4px 9px;
    white-space: nowrap;
`;

export const ElHeadlineAside = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
    flex: none;
`;

export const ElCoveragePeriod = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    color: ${Variables.ThemeLight.TextStrong};
    text-align: right;
    font-variant-numeric: tabular-nums;
`;

export const ElCoverageHint = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextXMild};
    text-align: right;
`;

export const ElSummaryRow = styled.div<{ $success?: boolean }>`
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid
        ${({ $success }) =>
            $success ? Variables.ThemeLight.SuccessMild2 : Variables.ThemeLight.Border2};
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

export const ElSectionTitle = styled.h2`
    margin: 0 0 16px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const ElFactsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px 28px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const ElFactCell = styled.div<{ $span?: number }>`
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    grid-column: ${({ $span }) => ($span ? `span ${$span}` : 'auto')};
`;

export const ElFactLabel = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const ElFactValue = styled.span<{ $mono?: boolean }>`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextStrong};
    text-wrap: pretty;
    word-break: ${({ $mono }) => ($mono ? 'break-all' : 'normal')};
    font-variant-numeric: ${({ $mono }) => ($mono ? 'tabular-nums' : 'normal')};
`;

export const ElSourceToggle = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 16px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const ElSourceGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px 28px;
    margin-top: 14px;
    padding-top: 16px;
    border-top: 1px solid ${Variables.ThemeLight.Border2};

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const ElBenefitsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    flex-wrap: wrap;
    gap: 8px;
`;

export const ElBenefitsSubtitle = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMild};
`;

export const ElBenefitsTable = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    overflow: hidden;
`;

export const ElBenefitsBody = styled.div<{ $scrollable?: boolean }>`
    ${({ $scrollable }) =>
        $scrollable
            ? `
        max-height: 400px;
        overflow-y: auto;
    `
            : ''}
`;

export const ElBenefitsHead = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1.6fr) 120px 110px 1.3fr;
    gap: 14px;
    padding: 9px 16px;
    background: ${Variables.ThemeLight.Background1};
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    font-family: ${Variables.Fonts.calibre};
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
`;

export const ElBenefitsRow = styled.div<{ $first?: boolean }>`
    display: grid;
    grid-template-columns: minmax(180px, 1.6fr) 120px 110px 1.3fr;
    gap: 14px;
    align-items: center;
    padding: 11px 16px;
    border-top: ${({ $first }) => ($first ? 'none' : `1px solid ${Variables.ThemeLight.Border2}`)};
`;

export const ElServiceName = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const ElCopay = styled.span<{ $muted?: boolean }>`
    text-align: right;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    color: ${({ $muted }) =>
        $muted ? Variables.ThemeLight.TextXMild : Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
`;

export const ElNote = styled.span<{ $hasNote?: boolean }>`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.3;
    color: ${({ $hasNote }) =>
        $hasNote ? Variables.ThemeLight.TextMedium : Variables.ThemeLight.TextXMild};
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

export const ElOtherRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-top: 16px;
    flex-wrap: wrap;
`;

export const ElOtherChips = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
`;

export const ElValidationCard = styled.div`
    margin-top: 12px;
    padding: 12px 14px;
    border-radius: 8px;
    background: ${Variables.ThemeLight.WarningMild1};
    border: 1px solid ${Variables.ThemeLight.WarningMild2};
`;

export const ElValidationItem = styled.p`
    margin: 0 0 6px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 13px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextStrong};

    &:last-child {
        margin-bottom: 0;
    }
`;

export const ElEmptyState = styled.p`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMild};
    font-style: italic;
`;
